import React, { useState, useEffect } from "react";
import moment from "moment";
import { getSourceStatusLeads } from "@/actions/reports";
import {
  Download,
  BarChart3,
  Users,
  Target,
  FileText,
} from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface SourceStatusData {
  sources: string[];
  statuses: string[];
  report: { [source: string]: { [status: string]: number } };
  sourceTotals: { [source: string]: number };
  statusTotals: { [status: string]: number };
  grandTotal: number;
}

const formatNumber = (num: number): string => {
  return num?.toLocaleString() || "0";
};

const OverviewReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [sourceStatusData, setSourceStatusData] = useState<SourceStatusData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      const response = await getSourceStatusLeads(params);
      if (response.success) {
        setSourceStatusData(response.data as SourceStatusData);
      }
    } catch (error) {
      console.error("Error fetching source status data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportSourceStatusMatrix = () => {
    if (!sourceStatusData) return;

    const headers = ["Source", ...sourceStatusData.statuses, "Total"];
    const csvRows = [headers.join(",")];

    sourceStatusData.sources.forEach((source) => {
      const row = [
        `"${source}"`,
        ...sourceStatusData.statuses.map(
          (status) => sourceStatusData.report[source]?.[status] || 0
        ),
        sourceStatusData.sourceTotals[source] || 0,
      ];
      csvRows.push(row.join(","));
    });

    // Add totals row
    const totalsRow = [
      "Total",
      ...sourceStatusData.statuses.map(
        (status) => sourceStatusData.statusTotals[status] || 0
      ),
      sourceStatusData.grandTotal,
    ];
    csvRows.push(totalsRow.join(","));

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `source-status-matrix-${fromDate}_${toDate}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader
          title="Overview"
          subtitle="Source & Status Matrix"
          icon={<BarChart3 className="h-5 w-5 text-white" />}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          onApplyFilter={fetchData}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader
        title="Overview"
        subtitle="Source & Status Matrix"
        icon={<BarChart3 className="h-5 w-5 text-white" />}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onApplyFilter={fetchData}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {!sourceStatusData ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No data available for the selected date range</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm gray-text">Total Leads</div>
                    <div className="text-2xl font-bold">{formatNumber(sourceStatusData.grandTotal)}</div>
                  </div>
                  <Users className="h-8 w-8 text-brandColor" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm gray-text">Lead Sources</div>
                    <div className="text-2xl font-bold">{sourceStatusData.sources.length}</div>
                  </div>
                  <Target className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm gray-text">Status Types</div>
                    <div className="text-2xl font-bold">{sourceStatusData.statuses.length}</div>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Source Status Matrix */}
            <div className="background-container rounded-lg shadow">
              <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Source vs Status Matrix
                </h3>
                <button
                  onClick={exportSourceStatusMatrix}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-brandColor text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Export to CSV
                </button>
              </div>
              <div className="p-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      {sourceStatusData.statuses.map((status) => (
                        <th key={status} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {status}
                        </th>
                      ))}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sourceStatusData.sources.map((source) => (
                      <tr key={source}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {source}
                        </td>
                        {sourceStatusData.statuses.map((status) => (
                          <td key={status} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatNumber(sourceStatusData.report[source]?.[status] || 0)}
                          </td>
                        ))}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatNumber(sourceStatusData.sourceTotals[source] || 0)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Total</td>
                      {sourceStatusData.statuses.map((status) => (
                        <td key={status} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(sourceStatusData.statusTotals[status] || 0)}
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(sourceStatusData.grandTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewReport;
